import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUp, ArrowDown, Pencil } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ListPaginationBar, ListPaginationControls } from '@/components/ui/ListPaginationBar'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { PolicyEditModal } from '../components/PolicyEditModal'
import { getPolicies, savePolicy } from '../services/policyService'
import {
  POLICY_GROUPS,
  type Policy,
  type PolicyListParams,
  type PolicySortKey,
  type PolicySubmitPayload,
  type SortDirection,
} from '../types'
import type { PolicyListPageProps } from '../types'

const DEFAULT_PARAMS: PolicyListParams = {
  orderBy: 'policy_name',
  sortBy: 'asc',
  page: 1,
  per_page: 15,
}

const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const
const PAGINATION_TEST_ID_PREFIX = 'policy-list'

export function PolicyListPage({ embedded = false }: PolicyListPageProps) {
  const [policies, setPolicies] = useState<Policy[]>([])
  const [params, setParams] = useState<PolicyListParams>(DEFAULT_PARAMS)
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [searchInput, setSearchInput] = useState('')
  const [groupInput, setGroupInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Policy | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const mountedRef = useRef(false)
  const groupInputRef = useRef('')
  groupInputRef.current = groupInput

  const load = useCallback(async (next: PolicyListParams) => {
    setLoading(true)
    try {
      const response = await getPolicies(next)
      setPolicies(response.data ?? [])
      setPagination({
        current: response.current ?? 1,
        last: response.last ?? 1,
        total: response.total ?? 0,
      })
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  const mergeFiltersAndLoad = useCallback(
    (q: string, policyGroup: string) => {
      const nextQ = q.trim() || undefined
      const nextG = policyGroup || undefined
      setParams((prev) => {
        const qEq = (prev.q ?? '') === (nextQ ?? '')
        const gEq = (prev.policy_group ?? '') === (nextG ?? '')
        if (qEq && gEq) return prev
        const next = { ...prev, q: nextQ, policy_group: nextG, page: 1 }
        void load(next)
        return next
      })
    },
    [load],
  )

  /** Search reloads while typing (debounced); group filter applies immediately (Mailtrap / Email Templates parity). */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      mergeFiltersAndLoad(searchInput, groupInputRef.current)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [searchInput, mergeFiltersAndLoad])

  const applyParams = useCallback(
    (next: PolicyListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: PolicySortKey) => {
    const nextDir: SortDirection =
      params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir, page: 1 })
  }

  const submitFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mergeFiltersAndLoad(searchInput, groupInput)
  }

  const applyGroupFilter = (nextGroup: string) => {
    setGroupInput(nextGroup)
    mergeFiltersAndLoad(searchInput, nextGroup)
  }

  const resetFilter = () => {
    setSearchInput('')
    setGroupInput('')
    const next = { ...params }
    delete next.q
    delete next.policy_group
    next.page = 1
    applyParams(next)
  }

  const goToPage = (pageNo: number) => applyParams({ ...params, page: pageNo })

  const changePerPage = (per_page: number) => {
    applyParams({ ...params, per_page, page: 1 })
  }

  const lastPage = Math.max(1, pagination.last)
  const currentPage = Math.min(Math.max(1, pagination.current), lastPage)
  const perPage = params.per_page ?? DEFAULT_PARAMS.per_page ?? 15
  const showNumericPagination =
    !loading && (pagination.total > 0 || policies.length > 0 || lastPage > 1)

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }

  const openEdit = (policy: Policy) => {
    setEditTarget(policy)
    setEditOpen(true)
  }

  const handleSubmit = async (payload: PolicySubmitPayload, policyKey?: string) => {
    setSubmitting(true)
    try {
      await savePolicy(payload, policyKey)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const SortIndicator = ({ column }: { column: PolicySortKey }) => {
    if (params.orderBy !== column) return null
    return params.sortBy === 'asc' ? (
      <ArrowUp className="inline h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="inline h-3 w-3 ml-1" />
    )
  }

  const addPolicyButton = (
    <button
      type="button"
      onClick={openAdd}
      className={primaryEntityActionButtonClass}
    >
      Add Policy
    </button>
  )

  const inner = (
    <div className="space-y-4">
      {embedded ? <div className="flex flex-wrap items-center justify-end gap-2">{addPolicyButton}</div> : null}
      <form onSubmit={submitFilter} className="flex flex-wrap gap-2">
        <input
          type="text"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search policy..."
          className="flex-1 min-w-[200px] max-w-md px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
        />
        <select
          value={groupInput}
          onChange={(event) => applyGroupFilter(event.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
        >
          <option value="">All groups</option>
          {(Object.keys(POLICY_GROUPS) as Array<keyof typeof POLICY_GROUPS>).map((key) => (
            <option key={key} value={key}>
              {POLICY_GROUPS[key]}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className={primaryEntityActionButtonClass}
        >
          Search
        </button>
        <button
          type="button"
          onClick={resetFilter}
          className={secondaryEntityActionButtonClass}
        >
          Reset
        </button>
      </form>

      <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-4">
        {showNumericPagination && (
          <div
            data-testid="policy-list-pagination-header-bar"
            className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-4"
          >
            <div
              data-testid="policy-list-pagination-header-summary"
              className="text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm"
            >
              Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
              <span className="font-bold text-slate-900">{lastPage}</span>
              <span className="mx-2 text-slate-300">|</span>
              Total <span className="font-bold text-slate-900">{pagination.total}</span> Records
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <ListPaginationControls
                currentPage={currentPage}
                totalPages={lastPage}
                onPageChange={goToPage}
                testIdPrefix={PAGINATION_TEST_ID_PREFIX}
                placement="header"
              />
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className={applicationStyles.listingTable}>
            <thead>
              <tr>
                <th aria-sort={params.orderBy === 'policy_name' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    className={applicationStyles.sortHeaderButton}
                    onClick={() => toggleSort('policy_name')}
                  >
                    <span className={applicationStyles.sortHeaderLabel}>Name</span>
                    <SortIndicator column="policy_name" />
                  </button>
                </th>
                <th aria-sort={params.orderBy === 'policy_group' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    className={applicationStyles.sortHeaderButton}
                    onClick={() => toggleSort('policy_group')}
                  >
                    <span className={applicationStyles.sortHeaderLabel}>Group</span>
                    <SortIndicator column="policy_group" />
                  </button>
                </th>
                <th aria-sort={params.orderBy === 'policy_description' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    className={applicationStyles.sortHeaderButton}
                    onClick={() => toggleSort('policy_description')}
                  >
                    <span className={applicationStyles.sortHeaderLabel}>Description</span>
                    <SortIndicator column="policy_description" />
                  </button>
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
              <ListingTableLoadingRow colSpan={4} />
              ) : policies.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No policies found.
                  </td>
                </tr>
              ) : (
                policies.map((policy) => (
                  <tr key={policy.policy_key ?? policy.policy_name}>
                    <td className="font-medium text-slate-900">{policy.policy_name}</td>
                    <td className="text-slate-700">
                      {(POLICY_GROUPS as Record<string, string>)[policy.policy_group as string] ??
                        policy.policy_group}
                    </td>
                    <td className="text-slate-700">{policy.policy_description}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => openEdit(policy)}
                        className="p-1.5 rounded text-[#1597ff] hover:bg-[#1597ff]/10"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showNumericPagination && (
          <ListPaginationBar
            placement="footer"
            currentPage={currentPage}
            lastPage={lastPage}
            totalRecords={pagination.total}
            perPage={perPage}
            onPageChange={goToPage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix={PAGINATION_TEST_ID_PREFIX}
            barDataTestId="policy-list-pagination-footer-bar"
          />
        )}
      </div>

      <PolicyEditModal
        isOpen={editOpen}
        policy={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />
    </div>
  )

  if (embedded) {
    return inner
  }

  return (
    <PageShell title="POLICY" headerAction={addPolicyButton}>
      {inner}
    </PageShell>
  )
}
